(ns frontend.ux-overhaul.kanban.view
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [frontend.utils :as fu]
   [oops.core :as oops]
   [frontend.zap-time :as zap-time]
   [reagent.core :as r]))

(defn adapt
  [component]
  (try
    (r/adapt-react-class component)
    (catch js/Object e
      (prn (.-message e)))))

;; Replaced dnd elements with divs to make the code compile,
;; so we can leave this implementation as reference,
;; even though beautiful-dnd was removed
(def drag-drop-context :div)
(def droppable :div)
(def draggable :div)

(def USE_SCROLL_SYNC false)

(defn recompute-row-heights!
  [_ {:keys [local-state column-index]}]
  (let [list-ref (get-in @local-state [:kanban :inner-list-ref column-index])
        update-fn (when list-ref
                    (aget list-ref "recomputeRowHeights"))]
    (when (fn? update-fn)
      (.call update-fn list-ref))))

(defn re-render-kanban-board
  [local-state]
  (when-let [board-ref (get-in @local-state [:kanban :board-ref])]
    (when-let [recompute-fn (aget board-ref "recomputeGridSize")]
      (.call recompute-fn board-ref)))
  (when-let [overlay-ref (get-in @local-state [:overlay :board-ref])]
    (when-let [recompute-fn (aget overlay-ref "recomputeGridSize")]
      (.call recompute-fn overlay-ref))))


(defn kanban-card
  [{:keys [data recompute-row-heights!]} card-settings]
  (let [card-size (-> card-settings
                      :look-and-feel
                      :card-size)
        card-height (condp = card-size
                      :compact "74px"
                      :medium "250px"
                      :large "450px")

        country (:country data)
        target (:target data)

        show-footer? (some->> card-settings
                              :card-footer
                              :show-footer?)

        color-coding (-> card-settings
                         :color-coding)
        default-color "#fafafa"
        color (if (-> color-coding
                      :use-color-coding?)
                (let [attribute (-> color-coding
                                    :attribute)
                      value (get data attribute)
                      color (-> color-coding
                                :matches
                                (get value)
                                (or default-color))]
                  color)
                default-color)]
    [:div.d-flex.flex-column.justify-content-between
     {:style {:height        card-height
              :min-width     "280px"
              :max-width     "280px"
              :background    "white"
              :box-shadow    "1px 1px 3px 1px #d4d4d4"
              :border-radius "5px"}}
     [:div.p-2.d-flex.justify-content-between {:style {:background color}}
      [:h3 {:style {:font-size "19px" :font-weight "300"}} (:header data)]
      [:input {:type "checkbox"}]]

     (when (or (= card-size :medium) (= card-size :large))
       [:div.d-flex.flex-column.p-2
        {:style {:overflow   "hidden"
                 :overflow-y "scroll"
                 :flex-grow  "2"
                 :background "white"}
         :class "hide-scrollbars"}
        [:div.mb-3
         [:p {:style {:color "#7a7a7a"}} "Target"]
         [:p {:style {:font-size "18px"}} target]]

        [:div.mb-3
         [:p {:style {:color "#7a7a7a"}} "Country"]
         [:p {:style {:font-size "18px"}} country]]])
     (when show-footer?
       [:div.p-2.d-flex.justify-content-between {:style {:background color}}
        [:i.fal.fa-archive]
        [:i.fal.fa-desktop]
        [:i.fal.fa-plane]
        [:div
         {:style    {:cursor "pointer"}
          :on-click (fn []
                      (when (fn? recompute-row-heights!)
                        (recompute-row-heights!)))}
         [:i.fal.fa-arrow-down]]
        [:p {:style {:font-size "18px"}}]])]))

(defn get-row-heights-sum
  [cache placeholder-index]
  (let [row-heights (js->clj (aget cache "_rowHeightCache") :keywordize-keys true)
        placeholder-kw (keyword (str placeholder-index))

        row-heights-without-placeholder (dissoc row-heights placeholder-kw)

        row-heights-sum (->> row-heights-without-placeholder
                             vals
                             vec
                             (reduce +))]
    row-heights-sum))

(defn get-row-height-sum-state
  [local-state column-index placeholder-index]
  (let [row-heights (or
                     (get-in @local-state [:kanban :cell-height column-index])
                     {})

        row-heights-without-placeholder (dissoc row-heights placeholder-index)

        row-heights-sum (->> row-heights-without-placeholder
                             vals
                             vec
                             (reduce +))]
    row-heights-sum))

(defn row-renderer
  [props js-args]
  (let [{:keys [card-renderer items cache board-height placeholder-index outer-column-index local-state]} props
        {:keys [is-visible columnIndex key parent style index isScrolling]} (js->clj js-args :keywordize-keys true)

        placeholder? (<= (count items) index)

        item (if placeholder?
               nil
               (nth items index))

        key (if item
              (-> item
                  :db/id
                  str)
              (str "placeholder-" outer-column-index "-" index))]
    (r/as-element
     [draggable
      {:key          key
       :draggable-id key
       :style        style
       :index        index}
      (fn
        [provided snapshot]
        (r/as-element
         [:div
          (merge
           {:style (merge style
                          (when placeholder?
                            {:pointer-events "none"}))}
           (js->clj (.-draggableProps provided))
           (js->clj (.-dragHandleProps provided))
           {:ref (.-innerRef provided)})
          (r/as-element
           [:> js/ReactVirtualized.CellMeasurer
            (merge
             {:cache        cache
              :column-index 0
              :key          key
              :parent       parent
              :row-index    index})
            (fn [props]
              (when (= index placeholder-index)
                (swap! local-state assoc-in [:kanban :cell-measurer-props outer-column-index] props))
              (r/as-element
               [:div
                {:class-name "kanban-card-container"}
                (if item
                  (card-renderer {:data         item
                                  :cache        cache
                                  :column-index outer-column-index
                                  :recompute-row-heights!
                                  (partial recompute-row-heights!
                                           props
                                           {:local-state  local-state
                                            :column-index outer-column-index})})
                  (when (and USE_SCROLL_SYNC placeholder?)
                    (let [max-col-height (or
                                          (get-in @local-state [:kanban :max-column-height])
                                          0)

                          row-heights-sum (get-row-height-sum-state
                                           local-state
                                           outer-column-index
                                           placeholder-index)

                          placeholder-height (max (- max-col-height row-heights-sum) 0)]
                      [:div
                       {:id    (str "placeholder-" outer-column-index)
                        :style {:max-height placeholder-height
                                :min-height placeholder-height
                                :overflow   "hidden"
                                    ;:background "red"
                                }}
                       #_[:div "placeholder top"]
                       #_[:div "placeholder center"]
                       #_[:div "placeholder bottom"]])))]))])]))])))

(defn calculate-drop-position
  [local-state]
  (let [kanban-id (get-in @local-state [:kanban :id])
        classname (str "kanban-" kanban-id "-drop-target")
        drop-targets (.getElementsByClassName js/document classname)
        drag-clone (.getElementById js/document "drag-clone")]
    (when (and drop-targets drag-clone)
      (let [rect (.getBoundingClientRect drag-clone)

            set-target-col (fn [target-column-index]
                             (swap! local-state assoc-in [:overlay :drop-target-column] target-column-index)

                             (fu/timeout 100
                                         (fn []
                                           (when-let [overlay-board-ref (get-in @local-state [:overlay :board-ref])]
                                             (oops/ocall+ overlay-board-ref "recomputeGridSize")))))

            clone-center-x (/ (+ (.-right rect) (.-left rect)) 2)
            clone-center-y (/ (+ (.-top rect) (.-bottom rect)) 2)

            drop-target (->> drop-targets
                             (filter (fn [dt]
                                       (let [rect (.getBoundingClientRect dt)
                                             left (.-left rect)
                                             right (.-right rect)
                                             top (.-top rect)
                                             bottom (.-bottom rect)]
                                         (and (< left clone-center-x right)
                                              (< top clone-center-y bottom)))))
                             (first))]
        (if-not drop-target
          (set-target-col nil)
          (let [drop-target-classes (.-className drop-target)

                ; Todo make this better
                target-index (some-> drop-target-classes
                                     (string/split #" ")
                                     (second)
                                     (string/split #"-")
                                     (nth 2)
                                     (int))
                previous-target (get-in @local-state [:overlay :drop-target-column])]
            (when (not= target-index previous-target)
              (set-target-col target-index))))))))

(defn re-render-columns
  [local-state]
  (let [inner-refs (get-in @local-state [:kanban :inner-list-ref])]
    (->> (vec (vals inner-refs))
         (map-indexed (fn [index ref]
                        (let [cell-measurer (get-in @local-state [:kanban :cell-measurer-props index])]
                          (when cell-measurer
                            (recompute-row-heights!
                             cell-measurer
                             {:local-state  local-state
                              :column-index index})))))
         (doall))))


(defn render-list
  [{:keys [group items column-index column-width column-count
           board-height card-renderer local-state]}
   custom-props]
  (let [items (or (:items custom-props) items)
        cache (js/ReactVirtualized.CellMeasurerCache.
               #js
                {:defaultHeight 150
                 :fixed-width   true
                 :fixedWidth    true
                 :keyMapper     (fn [row-index column-index]
                                  row-index)})
        droppable-id (str "droppable-group-" group)]
    (r/create-class
     {:component-did-mount (fn [])
      :reagent-render
      (fn []
        (r/as-element
         [droppable
          {:droppable-id     droppable-id
           :mode             "virtual"
           :type             "COLUMN"
           :is-drop-disabled true
           :render-clone     (fn [provided snapshot rubric]
                               (calculate-drop-position local-state)

                               (let [item-index (.-index (.-source rubric))
                                     props (js->clj (.-draggableProps provided))
                                     draggable-props (assoc-in props ["style" "opacity"] 1)
                                     item (nth items item-index)]

                                 (r/as-element
                                  ^{:key (str "field-item-key-"
                                              (-> item
                                                  :key
                                                  str))}
                                  [:div
                                   (merge
                                    {:id "drag-clone"}
                                    draggable-props
                                    (js->clj (.-dragHandleProps provided))
                                    {:ref (.-innerRef provided)})
                                   (card-renderer {:data item})])))}
          (fn
            [droppableProvided snapshot]
            (r/as-element
             [:> js/ReactVirtualized.AutoSizer
              (fn [m]
                (let [autosizer-height (aget m "height")
                      #_item-count
                      #_(if (.-isDragging snapshot)
                          (+ 1 (count items))
                          (count items))
                         ;item-count max-column-size

                      item-count (+ (count items) 1)

                      placeholder-index (count items)]
                  (r/as-element
                   (into
                    [:> js/ReactVirtualized.List
                     (merge
                      {:row-count          item-count
                       :width              column-width
                       :height             autosizer-height
                       :row-height         (fn [args]
                                             (let [args-clj (js->clj args :keywordize-keys true)
                                                   index (:index args-clj)
                                                   func (.-rowHeight cache)
                                                   height (.call func cache args)
                                                   result (if (= index placeholder-index)
                                                            height
                                                            (do
                                                              (swap! local-state assoc-in
                                                                     [:kanban :cell-height column-index index]
                                                                     height)
                                                              height))]
                                               result))
                       :on-rows-rendered   (fn [_]
                                             (let [row-heights-sum (get-row-heights-sum cache placeholder-index)
                                                   previous-max-h (or
                                                                   (get-in @local-state [:kanban :max-column-height])
                                                                   0)]
                                               (when (< previous-max-h row-heights-sum)
                                                 #_(js/console.log "set max h " row-heights-sum)
                                                 (swap! local-state assoc-in [:kanban :max-column-height] row-heights-sum)
                                                 (js/setTimeout
                                                  (fn [] (re-render-columns local-state))
                                                  100))))
                       :id                 (str "kanban-inner-column-" column-index)
                       :no-rows-renderer   (fn [] (r/as-element [:div ""]))
                       :overscan-row-count 10
                             ;:class-name
                       #_(when-not (= column-index (- column-count 1))
                           "hide-scrollbars")
                       :on-scroll
                       (fn [e]
                         (when USE_SCROLL_SYNC
                           (let [clj-event (js->clj e :keywordize-keys true)
                                 scroll-top (:scrollTop clj-event)

                                 row-heights-sum (get-row-heights-sum cache placeholder-index)

                                 prev-max-col-height (or
                                                      (get-in @local-state [:kanban :max-column-height])
                                                      0)

                                       ; Store max column height
                                 _ (when (< prev-max-col-height row-heights-sum)
                                     (swap! local-state assoc-in [:kanban :max-column-height] row-heights-sum))

                                 current-hover-col (get-in @local-state [:kanban :current-hover-column])]
                                   ; Scroll every list programmatically except the one that's being scrolled
                             (if-not current-hover-col
                               (->> (vals (get-in @local-state [:kanban :inner-list-ref]))
                                    (map-indexed (fn [index list-ref]
                                                   (when list-ref
                                                     (let [grid (aget list-ref "Grid")
                                                           container (aget grid "_scrollingContainer")]
                                                       (when container
                                                         (.scroll container 0 scroll-top))))))
                                    (doall))
                               (when (= current-hover-col column-index)
                                 (->> (vals (get-in @local-state [:kanban :inner-list-ref]))
                                      (map-indexed (fn [index list-ref]
                                                     (when (not= index column-index)
                                                       (when list-ref
                                                         (let [grid (aget list-ref "Grid")
                                                               container (aget grid "_scrollingContainer")]
                                                           (when container
                                                             (.scroll container 0 scroll-top)))))))
                                      (doall)))))))

                       :row-renderer       (partial row-renderer
                                                    {:items              items
                                                     :cache              cache
                                                     :outer-column-index column-index
                                                     :placeholder-index  placeholder-index
                                                     :board-height       board-height
                                                     :droppable-id       droppable-id
                                                     :droppable-provided droppableProvided
                                                     :item-count         item-count
                                                     :card-renderer      card-renderer
                                                     :local-state        local-state})
                       :ref                (fn [ref]
                                             (swap! local-state assoc-in [:kanban :inner-list-ref column-index] ref)
                                             (when ref
                                               (let [grid (aget ref "Grid")
                                                     container (aget grid "_scrollingContainer")]
                                                 (.innerRef droppableProvided container)
                                                 (swap! local-state assoc-in [:kanban :inner-list-node column-index] container))))})]))))]))]))})))

(defn default-column-renderer
  [own-props default-props]
  (let [{:keys [data config]} own-props
        {:keys [column-index render-list board-height is-drop-target?]} default-props
        column-label (-> data
                         :column-labels
                         (nth column-index))

        summary-footer-settings (-> config
                                    :view-settings
                                    :summary-footer)

        show-summary-footer? (:show-summary? summary-footer-settings)
        summary-footer-content (:content summary-footer-settings)

        column-content (-> data
                           :column-contents
                           (nth column-index))

        column-item-count (count column-content)

        ; Todo this needs to be generalized, only works for tasks now
        footer-content (->> summary-footer-content
                            (mapv (fn [kw]
                                    (condp = kw
                                      :total-count
                                      {:label (loc/tr :number-of-tasks)
                                       :value column-item-count}

                                      :number-of-overdue-tasks
                                      (let [now (zap-time/now)
                                            overdue-count (->> column-content
                                                               (filter (fn [item]
                                                                         (< (:task/due item) now)))
                                                               (count))]
                                        {:label (loc/tr :overdue-tasks)
                                         :value overdue-count})

                                      :number-of-completed-tasks
                                      (let [completed-count (->> column-content
                                                                 (filter (fn [item]
                                                                           (:task/completed? item)))
                                                                 (count))]
                                        {:label (loc/tr :completed-tasks)
                                         :value completed-count})

                                      :number-of-uncompleted-tasks
                                      (let [uncompleted-count (->> column-content
                                                                   (filter (fn [item]
                                                                             (not (:task/completed? item))))
                                                                   (count))]
                                        {:label (loc/tr :uncompleted-tasks)
                                         :value uncompleted-count})
                                      {}))))]
    [:div
     {:style {:height         board-height
              :display        "flex"
              :flex-direction "column"
              :background     "transparent"}}
     [:div
      {:class "py-2 flex justify-between items-center"}
      [:div
       {:class "text-base-light-text-paler truncate uppercase text-xs font-bold"}
       (str column-label)]
      [:div
       {:class "px-2 py-0.5 text-xs font-semibold bg-complement-light rounded"}
       (str column-item-count)]]
     [:div
      {:class "flex flex-col flex-1 overflow-hidden
               border border-solid border-complement-light-border
               bg-complement-light rounded"}

      [:div
       {:style {:height    "100%"
                :flex-grow "1"}}
       (when render-list [render-list config])]
      (when (and show-summary-footer? (< 0 (count footer-content)))
        (let [footer-height (condp = (count footer-content)
                              0 "0px"
                              1 "30px"
                              2 "60px"
                              3 "90px"
                              4 "120px")
              padding-top (condp = (count footer-content)
                            0 "0px"
                            1 "2px"
                            2 "3px"
                            3 "2px"
                            4 "2px")]
          [:div
           {:class "bg-base-gloomy font-bold"
            :style {:height      footer-height
                    :padding-top padding-top}}
           [:table
            {:style {:width "100%"}}
            [:tbody
             (->> footer-content
                  (map-indexed (fn [index {:keys [label value]}]
                                 (let [background (if (= (mod index 2) 0)
                                                    "transparent"
                                                    "transparent")]
                                   ^{:key label}
                                   [:tr
                                    (when (= column-index 0)
                                      [:td.px-3.py-1
                                       label])
                                    [:td.px-3.py-1.text-right
                                     value]])))
                  (doall))]]]))]]))

(defn kanban-cell-renderer
  [props js-args]
  (let [{:keys [data column-renderer board-height column-width
                card-renderer column-gap local-state column-count]}
        props
        {:keys [style columnIndex isScrolling]} (js->clj js-args :keywordize-keys true)
        items (-> data
                  :column-contents
                  (get columnIndex))]
    (r/as-element
     ^{:key (str "kanban-column-" columnIndex)}
     [:div
      {:style          (merge style
                              {:padding-right (str column-gap "px")})
       :on-mouse-enter (fn []
                         (swap! local-state assoc-in [:kanban :current-hover-column] columnIndex))
       :on-mouse-leave (fn []
                         (swap! local-state assoc-in [:kanban :current-hover-column] nil))}
      [column-renderer
       {:column-index columnIndex
        :board-height board-height
        :local-state  local-state
        :render-list  (partial render-list
                               {:group         columnIndex
                                :local-state   local-state
                                :items         items
                                :board-height  board-height
                                :column-width  column-width
                                :column-count  column-count
                                :column-index  columnIndex
                                :card-renderer card-renderer})}]])))


(defn kanban-board
  [{:keys [board-height column-width column-gap board-width column-count local-state
           data mouse-move-handler mouse-down-handler mouse-up-handler]
    :as   args}]
  (let [on-scroll (fn [e]
                    (let [clj-event (js->clj e :keywordize-keys true)
                          scroll-left (:scrollLeft clj-event)]
                      (swap! local-state assoc-in [:kanban :scroll-left] scroll-left)))]
    (r/as-element
     [:> js/ReactVirtualized.Grid
      {:height                board-height
       :column-count          column-count
       :column-width          (if column-gap
                                (+ column-width column-gap)
                                column-width)
       :id                    "kanban-board"
       :row-count             1
       :row-height            board-height
       :width                 board-width
       :style                 {:position "absolute" :z-index "3"}
       :overscan-column-count 5
       :on-scroll             on-scroll
       :ref                   (fn [ref]
                                (swap! local-state assoc-in [:kanban :board-ref] ref)

                                (when-let [kanban-el (.getElementById js/document "kanban-board")]
                                  (if (boolean ref)
                                    (do (.addEventListener kanban-el "mousemove" mouse-move-handler)
                                        (.addEventListener kanban-el "mousedown" mouse-down-handler)
                                        (.addEventListener kanban-el "mouseup" mouse-up-handler))

                                    (do (.removeEventListener kanban-el "mousemove" mouse-move-handler)
                                        (.removeEventListener kanban-el "mousedown" mouse-down-handler)
                                        (.removeEventListener kanban-el "mouseup" mouse-up-handler)))))
       :cell-renderer         (partial kanban-cell-renderer args)}])))

(defn overlay-column-renderer
  [props js-args]
  (let [{:keys [column-renderer column-gap board-height local-state]} props
        {:keys [style columnIndex]} (js->clj js-args :keywordize-keys true)
        drop-target-column (get-in @local-state [:overlay :drop-target-column])
        is-drop-target? (= columnIndex drop-target-column)]
    (r/as-element
     ^{:key (str "overlay-column-" columnIndex)}
     [:div {:style (merge style {:padding-right (str column-gap "px")})}
      [column-renderer
       {:column-index    columnIndex
        :board-height    board-height
        :local-state     local-state
        :is-drop-target? is-drop-target?}]])))

(defn overlay-draggable-renderer
  [props js-args]
  (let [{:keys [board-height column-width local-state]} props
        {:keys [style columnIndex]} (js->clj js-args :keywordize-keys true)
        kanban-id (get-in @local-state [:kanban :id])]
    (r/as-element
     [:div {:style style :key (str "overlay-draggable-item-" columnIndex)}
      (r/as-element
       [draggable
        {:key          (str "overlay-" columnIndex)
         :draggable-id (str "overlay-" columnIndex)
         :index        columnIndex}
        (fn
          [provided snapshot]
          (r/as-element
           [:div
            (merge
             {:class-name (str "drop-target" " " "drop-target-" columnIndex " " "kanban-" kanban-id "-drop-target")
              :style      {:overflow     "visible"
                           :display      "hidden"
                               ;:border       "1px solid blue"
                           :margin-right "-20px"
                           :height       board-height
                           :width        column-width}}
             (js->clj (.-draggableProps provided))
             (js->clj (.-dragHandleProps provided))
             {:ref (.-innerRef provided)})
            [:div.d-flex.flex-column
             {:style {:height "100%"}}]]))])])))

(defn overlay-board
  [{:keys [column-renderer board-height board-width local-state
           column-count column-width column-gap background-color]}]
  (let [on-scroll-droppable (fn [e]
                              (let [event (js->clj e :keywordize-keys true)
                                    droppable-scroll-left (:scrollLeft event)]
                                (if-let [overlay-grid-node (get-in @local-state [:overlay :board-node])]
                                  (.scroll overlay-grid-node droppable-scroll-left 0)
                                  (js/console.warn "overlay sync failed"))))
        is-dragging? (r/cursor local-state [:kanban :is-dragging?])]
    (r/as-element
     [:div
      {:style {:width      board-width
               :height     board-height
               :position   "absolute"
               :overflow   "visible"
               :background background-color
               :z-index    (if @is-dragging? "5" "-1")}}
      (r/as-element
       [:> js/ReactVirtualized.Grid
        {:height                board-height
         :column-count          column-count
         :column-width          (if column-gap
                                  (+ column-width column-gap)
                                  column-width)
         :row-count             1
         :style                 {:position "absolute"}
         :row-height            board-height
         :width                 board-width
         :overscan-column-count 3
         :cell-renderer         (partial overlay-column-renderer
                                         {:board-height    board-height
                                          :column-renderer column-renderer
                                          :column-gap      column-gap
                                          :column-width    column-width
                                          :local-state     local-state})
         :ref                   (fn [ref]
                                  (when ref
                                    (let [container (aget ref "_scrollingContainer")]
                                      (swap! local-state assoc-in [:overlay :board-node] container)
                                      (swap! local-state assoc-in [:overlay :board-ref] ref))))}])
      (r/as-element
       [droppable
        {:droppable-id "overlay-droppable"
         :type         "COLUMN"
         :direction    "horizontal"
         :mode         "virtual"
         :render-clone (fn [provided snapshot rubric]
                         (let [item-index (.-index (.-source rubric))
                               snap-clj (js->clj snapshot :keywordize-keys true)]
                           (r/as-element
                            ^{:key (str "clone-item-key-" item-index)}
                            [:div
                             (merge
                              (js->clj (.-draggableProps provided))
                              (js->clj (.-dragHandleProps provided))
                              {:ref (.-innerRef provided)})
                             [:div]])))}
        (fn
          [droppableProvided snapshot]
          (r/as-element
           [:> js/ReactVirtualized.Grid
            {:height                (- board-height 52)
             :column-count          column-count
             :column-width          (if column-gap
                                      (+ column-width column-gap)
                                      column-width)
             :row-count             1
             :style                 {:position "absolute" :top 55} ; Todo top needs to be dynamic depending on column header height
             :row-height            (- board-height 55)
             :class-name            "hide-scrollbars"
             :width                 board-width
             :on-scroll             on-scroll-droppable
             :overscan-column-count 1
             :cell-renderer         (partial overlay-draggable-renderer
                                             {:board-height (- board-height 100)
                                              :column-width column-width
                                              :local-state  local-state})
             :ref                   (fn [ref]
                                      (when ref
                                        (let [container (aget ref "_scrollingContainer")]
                                          (. droppableProvided innerRef container)
                                          (swap! local-state assoc-in [:overlay :droppable-node] container))))}]))])])))

(defn view
  [{:keys [kanban-id card-renderer board-height board-width
           column-width column-gap column-count column-renderer
           background-color on-drag-end data]}]
  (let [local-state (r/atom {:kanban        {:is-dragging? false
                                             :scroll-left  0
                                             :id           kanban-id
                                             :board-ref    nil
                                             ; other stuff goes here also
                                             }
                             :overlay       {:droppable-node     nil
                                             :board-node         nil
                                             :board-ref          nil
                                             :drop-target-column nil}
                             :expand-group  {0 false}
                             :container-ref nil
                             :container-key 1})
        mouse-position-state (r/atom {:x 0 :y 0})

        scroll-overlay-to (fn [left]
                            (when-let [overlay-droppable-node (get-in @local-state [:overlay :droppable-node])]
                              (.scroll overlay-droppable-node left 0)))

        mouse-move-handler (fn [e]
                             (let [mouse-x (.-clientX e)
                                   mouse-y (.-clientY e)
                                   kanban-el (.getElementById js/document "kanban-board")
                                   kanban-rect (.getBoundingClientRect kanban-el)
                                   kanban-x (.-left kanban-rect)
                                   kanban-y (.-top kanban-rect)
                                   x (- mouse-x kanban-x)
                                   y (- mouse-y kanban-y)]
                               (reset! mouse-position-state {:x x :y y})))

        mouse-down-handler (fn []
                             (let [{:keys [y]} @mouse-position-state
                                   diff-from-bottom (- board-height y)]
                               ; Todo - This expects scrollbar height to be 23. Is there a way to get the exact value?
                               (when (< 23 diff-from-bottom)
                                 (when-let [kanban-el (.getElementById js/document "kanban-board")]
                                   (.add (.-classList kanban-el) "kanban-prevent-scroll")))))

        mouse-up-handler (fn []
                           (when-let [kanban-el (.getElementById js/document "kanban-board")]
                             (.remove (.-classList kanban-el) "kanban-prevent-scroll")))

        handle-drag-start (fn [e]
                            (mouse-up-handler)
                            (let [event (js->clj e :keywordize-keys true)
                                  {:keys [draggableId source]} event
                                  board-ref (get-in @local-state [:kanban :board-ref])
                                  board-node (aget board-ref "_scrollingContainer")
                                  board-scroll-left (.-scrollLeft board-node)]
                              (scroll-overlay-to board-scroll-left)
                              (swap! local-state assoc-in [:kanban :is-dragging?] true)))

        handle-drag-end (fn [e]
                          (let [event (js->clj e :keywordize-keys true)
                                draggable-id (:draggableId event)]
                            (swap! local-state assoc-in [:kanban :is-dragging?] false)
                            (if (string/starts-with? (str draggable-id) "overlay")
                              (js/console.log "Dragged overlay item")

                              ; Scroll original board to overlay position
                              (let [overlay-board-node (get-in @local-state [:overlay :board-node])
                                    overlay-scroll-left (.-scrollLeft overlay-board-node)
                                    kanban-grid-ref (get-in @local-state [:kanban :board-ref])
                                    grid-node (aget kanban-grid-ref "_scrollingContainer")]

                                (.scroll grid-node overlay-scroll-left 0)

                                (swap! local-state assoc-in [:overlay :drop-target-column] nil)

                                (let [clj-event (js->clj e)
                                      source-group (-> (get (get clj-event "source") "droppableId")
                                                       (string/split #"-")
                                                       (nth 2)
                                                       (int))

                                      draggable-id (js/parseInt (get clj-event "draggableId"))

                                      destination (int (get (get clj-event "destination") "index"))

                                      target-column-id (when (< destination column-count)
                                                         (-> data
                                                             :column-values
                                                             (nth destination)))]
                                  (when (and destination (< destination column-count))
                                    (on-drag-end {:item-id          draggable-id
                                                  :source-col-index source-group
                                                  :target-col-index destination
                                                  :target-col-id    target-column-id}))

                                  (js/setTimeout
                                   (fn []
                                     (swap! local-state assoc-in [:kanban :is-dragging?] false)
                                     (js/setTimeout
                                      (fn []
                                        (when-let [kanban-grid-ref (get-in @local-state [:kanban :board-ref])]
                                          (let [recompute-fn (aget kanban-grid-ref "measureAllCells")
                                                other-recompute (aget kanban-grid-ref "recomputeGridSize")]
                                            (.call other-recompute kanban-grid-ref 0 0)
                                            (.call recompute-fn kanban-grid-ref))))
                                      100))
                                   20))))))]
    [drag-drop-context
     {:onDragStart handle-drag-start
      :onDragEnd   handle-drag-end}
     [:div
      {:class "flex flex-col px-4"
       :style {:position "relative"
               :overflow "hidden"
               :width    board-width
               :height   board-height}}
      [overlay-board
       {:data             data
        :column-renderer  column-renderer
        :board-width      board-width
        :column-width     column-width
        :column-gap       column-gap
        :board-height     board-height
        :column-count     column-count
        :background-color background-color
        :local-state      local-state}]
      [:div
       {:style {:width      "100%"
                :height     "100%"
                :position   "absolute"
                :z-index    "1"
                :background background-color}}]
      [kanban-board
       {:board-height         board-height
        :data                 data
        :column-count         column-count
        :column-width         column-width
        :column-gap           column-gap
        :board-width          board-width
        :column-renderer      column-renderer
        :card-renderer        card-renderer
        :local-state          local-state
        :mouse-move-handler   mouse-move-handler
        :mouse-down-handler   mouse-down-handler
        :mouse-up-handler     mouse-up-handler
        :mouse-position-state mouse-position-state}]]]))
